/**
 * Timesheet Management System Types
 */

export enum TimesheetStatus {
  DRAFT = 'Draft',
  SUBMITTED = 'Submitted',
  APPROVED = 'Approved',
  RETURNED = 'Returned',
}

export enum ProjectStatus {
  ACTIVE = 'Active',
  COMPLETED = 'Completed',
  ON_HOLD = 'On Hold',
}

export interface Project {
  id: string;
  name: string;
  code: string;
  clientName: string;
  startDate: string;
  endDate: string;
  status: ProjectStatus;
  billable: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Task {
  id: string;
  projectId: string;
  name: string;
  description: string;
  billable: boolean;
  isActive: boolean;
  isDefault: boolean;
  createdAt: string;
}

export interface ProjectMember {
  projectId: string;
  employeeId: string;
  employeeName: string;
  assignedAt: string;
}

export interface TimeEntry {
  id: string;
  employeeId: string;
  employeeName: string;
  weeklyTimesheetId: string;
  projectId: string;
  projectName: string;
  taskId: string;
  taskName: string;
  date: string;
  hours: number;
  description: string;
  billable: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface WeeklyTimesheet {
  id: string;
  employeeId: string;
  employeeName: string;
  managerId: string;
  weekStartDate: string;
  weekEndDate: string;
  status: TimesheetStatus;
  totalHours: number;
  submittedAt?: string;
  approvedAt?: string;
  approvedBy?: string;
  returnedReason?: string;
  createdAt: string;
  updatedAt: string;
}

export interface TimesheetPolicy {
  configId: string;
  standardHoursPerDay: number;
  workingDays: string[];
  retroactiveLimitWeeks: number;
  submissionDeadline: string;
  autoReminders: boolean;
  createdAt: string;
  updatedAt: string;
}

// Input types
export interface CreateProjectInput {
  name: string;
  code: string;
  clientName: string;
  startDate: string;
  endDate: string;
  billable: boolean;
}

export interface CreateTaskInput {
  projectId: string;
  name: string;
  description?: string;
  billable: boolean;
}

export interface CreateTimeEntryInput {
  weeklyTimesheetId: string;
  projectId: string;
  projectName: string;
  taskId: string;
  taskName: string;
  date: string;
  hours: number;
  description?: string;
  billable: boolean;
}

export interface UpdateTimeEntryInput {
  id: string;
  hours?: number;
  description?: string;
}

// Connection types
export interface ProjectConnection {
  items: Project[];
  nextToken?: string;
}

export interface WeeklyTimesheetConnection {
  items: WeeklyTimesheet[];
  nextToken?: string;
}

function formatISODate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseISODate(date: string): Date {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(year, month - 1, day);
}

// Helper: get week dates
export function getWeekDates(date: Date): { start: string; end: string; days: string[] } {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday
  const monday = new Date(d.setDate(diff));
  const days: string[] = [];
  for (let i = 0; i < 7; i++) {
    const dd = new Date(monday);
    dd.setDate(monday.getDate() + i);
    days.push(formatISODate(dd));
  }
  return {
    start: days[0],
    end: days[4], // Friday
    days: days.slice(0, 5), // Mon-Fri
  };
}

export function formatWeekLabel(start: string): string {
  const s = parseISODate(start);
  const e = new Date(s);
  e.setDate(s.getDate() + 4);
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  return `${s.toLocaleDateString('en-US', opts)} — ${e.toLocaleDateString('en-US', { ...opts, year: 'numeric' })}`;
}

export interface WeekOption {
  label: string;
  relativeLabel: string;
  startDate: string;
  endDate: string;
}

export function getLastNWeeks(count = 8): WeekOption[] {
  return Array.from({ length: count }, (_, index) => {
    const date = new Date();
    date.setDate(date.getDate() - index * 7);
    const week = getWeekDates(date);

    return {
      label: formatWeekLabel(week.start),
      relativeLabel: index === 0 ? 'Current Week' : `Previous Week ${index}`,
      startDate: week.start,
      endDate: week.end,
    };
  });
}
